import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  login as loginAction,
  logout as logoutAction,
  refreshSession as refreshAction,
} from './authSlice';
import type { AuthState, AuthSession, User } from './types';

type RootWithAuth = { auth: AuthState };

/**
 * Slim Redux-binding hook for the auth slice.
 *
 * The actual API calls (login, refresh, revoke) live in `@kss/api`'s
 * `authService` so that `@kss/auth` doesn't take a hard dependency on the
 * HTTP layer. Components call `authService.login(...)` themselves and
 * pass the result here as `(user, session)`.
 *
 * `state` is spread onto the return value so existing code reading
 * `useAuth().isAuthenticated` / `useAuth().user` keeps working.
 */
export function useAuth() {
  const dispatch = useDispatch();
  const state = useSelector((s: RootWithAuth) => s.auth);

  return {
    ...state,
    login: useCallback(
      (user: User, session: AuthSession) => dispatch(loginAction({ user, session })),
      [dispatch]
    ),
    refresh: useCallback(
      (session: AuthSession) => dispatch(refreshAction(session)),
      [dispatch]
    ),
    logout: useCallback(() => dispatch(logoutAction()), [dispatch]),
  };
}
